import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { of } from 'rxjs';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import { LookupOption, LookupTypeCodes } from '../../../models/lookup.models';
import { NotesWithMentionsDto } from '../../../models/notes.models';
import { TaskAssignmentDto } from '../../../models/request.models';
import { PracticeAreaDto } from '../../../models/resource.models';
import { AssignmentService } from '../../../services/assignment.service';
import { AuthService } from '../../../services/auth.service';
import { FeatureFlagService } from '../../../services/feature-flag.service';
import { LookupService } from '../../../services/lookup.service';
import { SnackbarService } from '../../../services/snackbar.service';
import { describeApiError } from '../../../utils/api-error.utils';
import { toAssignmentRow } from '../../../utils/assignment-row.utils';
import { sortAssignmentStatusOptions } from '../../../utils/assignment-status.utils';
import {
  AssignmentEditDialogComponent,
  AssignmentEditDialogData,
  AssignmentEditDialogResult,
} from '../assignment-edit-dialog/assignment-edit-dialog.component';
import { MatterCellComponent } from '../matter-cell/matter-cell.component';
import { MembershipChipsComponent } from '../membership-chips/membership-chips.component';
import { RowExpansion } from '../row-expansion/row-expansion';
import { TaskAssignmentListComponent } from '../task-assignment-list/task-assignment-list.component';

/**
 * Gates the task-assignment column and its detail panel. Matches the backend's
 * `FeatureFlagKeys.TaskAssignments`; seeded disabled, so the card renders exactly as it did
 * before this feature until someone turns it on.
 */
const TASK_ASSIGNMENTS_FLAG = 'task-assignments';

/**
 * Gates the Duration -> Last Billed Date column swap and the rendering of the API's synthetic
 * SYSTEM/AUTO rows. Matches the backend's `FeatureFlagKeys.AssignmentsLatestBilled`; seeded
 * disabled, so the card renders exactly as before until someone turns it on.
 */
const LATEST_BILLED_FLAG = 'assignments-latest-billed';

/**
 * The statuses the row menu offers as "Mark X" items, by code. Codes only: the labels,
 * and which of these are live, come from the ASSIGNED_RESOURCE_STATUS lookup — the same
 * cached source the edit dialog's status select reads — so a reseeded name or a retired
 * value reaches the menu without a change here, and the label written onto a row is
 * the one the rest of the UI shows. PENDING is deliberately absent: nothing should be
 * sent back to pending from a one-click action, and the dialog already withholds it
 * from request-linked rows.
 */
const QUICK_STATUS_CODES: readonly string[] = ['ACTIVE', 'INACTIVE', 'CLOSED'];

/**
 * Unified row model for the assignments table.
 * In "resource" mode the first column shows client/matter info.
 * In "matter" mode the first column shows the assigned resource.
 */
export interface AssignmentRow {
  /** Originating Request id, if the assignment is still tied to a Request. */
  requestId?: number;
  status: string;
  statusName: string;
  startDate?: Date;
  endDate?: Date;
  /**
   * When the resource last billed this row's matter (assignments-latest-billed
   * flag). On synthetic SYSTEM/AUTO rows it is the row's reason to exist; on
   * real rows it is enrichment and may be absent.
   */
  lastBilledDate?: Date | string;
  assignmentTypeCategory: string;
  assignmentTypeCategoryName: string;

  // Resource-mode fields (shown when mode === 'resource')
  clientMatterNumber?: string;
  clientName?: string;
  matterTitle?: string;

  // Matter-mode fields (shown when mode === 'matter')
  employeeId?: string;
  fullName?: string;
  title?: string;

  // Notes
  assignedResourceId?: number;
  hasNotes?: boolean;
  notesInfo?: NotesWithMentionsDto | null;
  hasResourceNotes?: boolean;
  resourceNotesInfo?: NotesWithMentionsDto | null;
  dialogTitle?: string;
  resourceName?: string;

  // Permissions
  canViewResource?: boolean;
  canViewRequest?: boolean;
  /**
   * Set only by consumers whose rows are the current user's by construction (the
   * dashboard's My Assignments). Leave unset otherwise: the card then derives
   * ownership from `employeeId` at the moment the dialog opens, which avoids
   * baking in an identity that may not have loaded yet when the rows were built.
   */
  isOwnAssignment?: boolean;

  // Practice areas to render as chips in the Practice Areas column.
  practiceAreas?: PracticeAreaDto[];

  /**
   * Matter tasks this resource is assigned to, carried through from the assignment DTO.
   * Optional here, unlike on the DTO: a row can be built by a consumer that has no task
   * data, so treat undefined and [] alike as "no tasks".
   */
  taskAssignments?: TaskAssignmentDto[];
}

export type AssignmentsCardMode = 'resource' | 'matter';

@Component({
  selector: 'app-assignments-card',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatCardModule,
    MatDialogModule,
    MatIconModule,
    MatMenuModule,
    MatPaginatorModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    RouterModule,
    MembershipChipsComponent,
    MatterCellComponent,
    TaskAssignmentListComponent,
  ],
  templateUrl: './assignments-card.component.html',
  styleUrls: ['./assignments-card.component.scss'],
})
export class AssignmentsCardComponent {
  private dialog = inject(MatDialog);
  private authService = inject(AuthService);
  private featureFlagService = inject(FeatureFlagService);
  private assignmentService = inject(AssignmentService);
  private snackbar = inject(SnackbarService);
  private lookupService = inject(LookupService);

  /**
   * Ends the card's own subscriptions with the card. A status write, a create, or a
   * dialog result that answered after the card was gone would otherwise patch a row
   * nothing renders, emit on a destroyed output, and drop a snackbar on whatever page
   * the user is looking at by then. finalize still runs on that teardown, so an
   * in-flight row is released either way.
   */
  private readonly destroyRef = inject(DestroyRef);

  /**
   * Rows with a row-menu write in flight — a status change, or the create that turns
   * a synthetic row into an assignment. The row's menu trigger is disabled while it is
   * here, so a second click cannot race the first: two writes for one row would leave
   * it showing whichever answered last, and two creates would be a duplicate the API
   * has to refuse. Keyed by row identity rather than assignment id because a synthetic
   * row has no id until its create lands.
   */
  private readonly updatingRows = new Set<AssignmentRow>();

  /**
   * Which rows have their task list open, and the DOM id of each row's panel.
   *
   * Counting only the tasks means the label matches the panel's lines. Zero is a real answer —
   * a resource assigned to the matter but to none of its tasks — so those rows still expand,
   * onto the list's own "No tasks assigned" line, and the label is simply left off.
   *
   * Declared above the flag below, which clears it: field initializers run in order.
   */
  readonly expansion = new RowExpansion<AssignmentRow>(
    (row) => row.taskAssignments?.length ?? 0,
    'task',
    'task-assignments-panel',
  );

  /**
   * Whether the task-assignment column and its detail rows are offered. Resolved here rather
   * than passed in by each consumer: three pages render this card, and a flag read three times
   * is three chances for one of them to drift.
   *
   * isFeatureFlagEnabled seeds its subject with `false` and only then fetches, so the first
   * value always says "off" and the column appears a beat later. That is the right default for
   * a gate whose whole job is to hide something — and unlike the dashboard, nothing here
   * persists off the seeded value (no tab demotion, no URL or cookie write), so the transient
   * "off" costs nothing. Kept live rather than read once so an admin toggling the flag is
   * reflected without a reload.
   *
   * A flag going off also closes whatever is already open, as the billing cards do: the toggle
   * that would have closed it is the very thing being withdrawn, so an expansion left standing
   * would be unreachable state — and one that reappears if the flag comes back.
   */
  readonly showTaskAssignments = toSignal(
    this.featureFlagService.isFeatureFlagEnabled(TASK_ASSIGNMENTS_FLAG).pipe(
      tap((enabled) => {
        if (!enabled) this.expansion.clear();
      }),
    ),
    { initialValue: false },
  );

  /**
   * Every active ASSIGNED_RESOURCE_STATUS option, in the order the edit dialog lists
   * them, read from the lookup's shared cache. Empty until the lookup answers — a menu
   * opened in that first moment holds only "Edit assignment" — and empty for good if
   * the lookup fails, rather than an error thrown from the template on every read: the
   * dialog still offers the statuses, and the failure is already logged by the service.
   */
  readonly statusOptions = toSignal(
    this.lookupService
      .getLookupOptions(LookupTypeCodes.ASSIGNED_RESOURCE_STATUS)
      .pipe(
        map(sortAssignmentStatusOptions),
        catchError(() => of([] as LookupOption[])),
      ),
    { initialValue: [] as LookupOption[] },
  );

  /**
   * Gates the Duration -> Last Billed Date column swap on the assignments-latest-billed
   * flag. With the flag off the API sends no lastBilledDate (and no SYSTEM/AUTO rows), so
   * the card keeps showing Duration rather than a column of empty cells. Same live-read
   * pattern as showTaskAssignments above, and the same transient first-read "off".
   */
  readonly latestBilledEnabled = toSignal(
    this.featureFlagService.isFeatureFlagEnabled(LATEST_BILLED_FLAG),
    { initialValue: false },
  );

  constructor() {
    // Consumers hide the filter while their own add form is open. Drop every row
    // filter along with it — the text and the practice-area chips alike: a filter
    // that survived would come back the moment the form closes and hide the row
    // just added, which reads as the create having failed. Clearing rather than
    // merely suspending is deliberate; a chip left rendered as selected while it
    // is not filtering claims a filter that isn't applied.
    effect(() => {
      if (!this.showFilter()) this.resetFilters();
    });

    // Prune chip selections that no longer have a chip. Removing the last
    // assignment in a selected area rebuilds practiceAreaSummary without it,
    // and the selection would go on filtering with nothing left to click.
    // Keys that still render are kept, so a reload after an unrelated add or
    // remove leaves the user's filter intact.
    //
    // Since the summary is view-scoped this also fires on the Active/Past flip:
    // an area with no rows in the view being entered has no chip there, so the
    // selection goes with it and that view opens unfiltered. The alternative is
    // the no-match empty state with no chip to deselect.
    effect(() => {
      const live = new Set(this.practiceAreaSummary().map((item) => item.key));
      for (const key of this.selectedPracticeAreas) {
        if (!live.has(key)) this.selectedPracticeAreas.delete(key);
      }
    });

    // A wholesale change of record is not a reload: filters chosen for the
    // previous matter mean nothing on the next one. Consumers that reuse a
    // single card instance across records bind filterResetKey so this fires.
    //
    // currentView is part of that state and the least visible half: a stale text
    // filter at least shows its text in a box, while a stale Past tab presents
    // an empty table under a plausible "No past assignments" message on a record
    // that has active work. Reset here only — doing it when showFilter drops
    // would flip the tab out from under someone opening the add form.
    effect(() => {
      this.filterResetKey();
      this.resetFilters();
      this.currentView.set('active');
    });

    // clampedPageIndex only clamps the value the template reads; the stored
    // index survives a shrink and re-applies the moment the list grows back,
    // jumping the user forward to a page they never selected. Fold the clamp
    // into state on every list change — reading clampedPageIndex tracks
    // assignments() transitively. The clamp only ever lowers the index, so it
    // cannot undo the reset above.
    effect(() => {
      // An in-place status change can shrink the list as well — a row marked
      // Closed leaves the Active view — and only rowsVersion announces those.
      this.rowsVersion();
      this.pageIndex = this.clampedPageIndex;
    });

    // A new list is a new set of row objects: the panel ids handed out for the
    // old ones mean nothing, and expansion keyed on rows nothing renders any more
    // would be retained for the component's lifetime. Prepared here, once per
    // result set, so panelId() stays a pure read in the template.
    //
    // Deliberately its own effect rather than folded into the clamp above: that
    // one reads clampedPageIndex, so it also re-runs on a page change, a view
    // flip and every keystroke in the filter box — all of which must LEAVE open
    // rows open. This one tracks the input alone, which is the only thing that
    // invalidates the row identities. It prepares every assignment, not just the
    // current page, so paging to a row does not find it without an id.
    effect(() => {
      const rows = this.assignments();
      this.expansion.clear();
      this.expansion.prepare(rows);
    });
  }

  /** Whether to show a Client Matter column (resource) or a Resource column (matter). */
  readonly mode = input<AssignmentsCardMode>('resource');

  /** The rows to display in the table. */
  readonly assignments = input<AssignmentRow[]>([]);

  /** Whether data is still loading. */
  readonly isLoading = input(false);

  /** Whether the current user can edit assignments via the dialog. */
  readonly canEdit = input(false);

  /** Whether the current user can remove direct (non-request) assignments. */
  readonly canRemove = input(false);

  /**
   * Whether the current user may create direct assignments (Assignments.Create). Gates
   * the row menu on synthetic latest-billed rows, whose "Mark X" items create the
   * assignment rather than update one — the same permission the header's add button
   * rides on, bound separately because that button is a visibility choice the parent
   * owns (showAddAssignment) while this is the permission itself.
   */
  readonly canCreate = input(false);

  /** Whether the manager-note section is offered in the dialog. Hidden entirely when false. */
  readonly showManagerNotes = input(true);

  /**
   * Whether the current user may write the manager note
   * (Assignments.ManagerNotes.Write). Independent of canEdit, which covers
   * status/dates/type (Assignments.Edit) — the API gates the manager-note
   * PUT/DELETE on the Write permission alone, so a Read-only holder gets the
   * note as a read-only field rather than one whose save would 403.
   */
  readonly canWriteManagerNotes = input(false);

  /**
   * Whether the current user may view other resources' own assignment notes
   * (Assignments.ResourceNotes.Read). A user's own notes are always visible
   * to them regardless of this flag.
   */
  readonly canReadResourceNotes = input(false);

  /** Whether to render the built-in "Assignments" header. Set false when the consumer supplies its own. */
  readonly showHeader = input(true);

  /** Whether the client/matter cell renders as a link (true) or plain text (false). */
  readonly canViewMatter = input(true);

  readonly canViewRequest = input(true);

  /** Whether the Request Id column is visible. */
  readonly showRequestId = input(true);

  /** Whether the table paginates. Compact consumers (dashboard) opt out. */
  readonly showPaginator = input(true);

  /**
   * Whether the row filters are offered. Compact consumers (dashboard) opt out
   * permanently; the detail pages drop it while their add form is open.
   *
   * Governs both row filters, not just the text box it is named for: the
   * practice-area chips go disabled with it, and a chip selection stops
   * filtering. One input rather than two because no consumer wants the two
   * suspended independently — they suspend for the same reason, that a filter
   * the user cannot operate must not hide the row they just added.
   */
  readonly showFilter = input(true);

  /**
   * Identity of the record these assignments belong to (the matter number, the
   * resource id). Consumers that reuse one card instance across records — a
   * route-param change swaps the list without recreating the component — bind
   * it so the card can tell "different record" from "same record, reloaded"
   * and drop the filters only in the former case. Left unbound, filters persist.
   */
  readonly filterResetKey = input<string | number | null | undefined>(undefined);

  /**
   * Whether the header offers the "+ Add assignment" button. The parent owns the
   * add flow (it is projected content), so it also decides visibility — typically
   * the caller's create permission.
   */
  readonly showAddAssignment = input(false);

  /**
   * Whether that button is disabled. Kept separate from visibility so the parent
   * can grey it out while its add form is already open: the button stays in place
   * rather than vanishing, which would shift the header contents mid-interaction.
   */
  readonly addAssignmentDisabled = input(false);

  /** Emitted when the user clicks the header's "+ Add Assignment" button. */
  readonly addAssignmentClicked = output<void>();

  /** Emitted when the user clicks the client/matter link (resource mode). */
  readonly matterClicked = output<string>();

  /** Emitted when the user clicks the resource link (matter mode). */
  readonly resourceClicked = output<string>();

  /** Emitted when a row is removed via the edit dialog (already deleted; parent should refresh). */
  readonly assignmentRemoved = output<AssignmentRow>();

  /** Legacy: emitted when a row should be removed via parent-driven confirmation. */
  readonly removeClicked = output<AssignmentRow>();

  /**
   * Emitted when a row has been patched in place: its editable fields changed via the
   * edit dialog or the row menu, or a synthetic latest-billed row became a real
   * assignment. Always the same row object the consumer handed in, never a copy.
   */
  readonly assignmentChanged = output<AssignmentRow>();

  /** Optional override for the empty-state message. */
  readonly emptyMessageOverride = input<string | undefined>(undefined);

  /** Whether to show the per-practice-area assignment count pills above the table. */
  readonly showPracticeAreaSummary = input(false);

  /**
   * Whether this host offers the "View All Notes" header action at all — set by
   * the pages that render the Assignment Notes card and handle
   * viewAllNotesClicked; left false everywhere else.
   *
   * Deliberately NOT a permission flag. Which notes a viewer may see is already
   * decided by the API: hasNotes and hasResourceNotes are only ever true for a
   * note the caller is allowed to know about — including the viewer's own
   * resource note, which needs no permission at all. Gating this input on a
   * permission as well would hide the action from a resource who has nothing
   * but their own note, which is exactly who the "My Note" view is for.
   */
  readonly showViewAllNotes = input(false);

  /** Emitted when the user clicks the "View All Notes" header action. */
  readonly viewAllNotesClicked = output<void>();

  /**
   * Whether a single row carries a note of either kind. Named apart from the
   * row's own `hasNotes` field, which means manager notes only.
   */
  rowHasAnyNote(row: AssignmentRow): boolean {
    return !!(row.hasNotes || row.hasResourceNotes);
  }

  /**
   * Bumped by every in-place patch of a row (see rowPatched). Rows are mutated
   * where they stand rather than replaced — the expansion tracks them by identity
   * — so assignments() itself never changes when a status does. The plain getters
   * behind the table pick the mutation up on the next check regardless; the
   * reactive graph does not, so any computed or effect that derives from row
   * FIELDS reads this to be told. Without it a matter's only Litigation row marked
   * Closed leaves the Active table while the chip still reads "Litigation - 1",
   * and clicking it lands on the no-match empty state.
   */
  private readonly rowsVersion = signal(0);

  /**
   * Per-practice-area assignment counts, one entry per distinct practice area
   * in the CURRENT view (Active or Past). An assignment with multiple practice
   * areas counts toward each.
   *
   * Scoped to the view rather than the whole list because each chip is also the
   * button that filters by that area, and a click only ever narrows the current
   * view. A count taken across both views would advertise rows a click cannot
   * produce — "Litigation - 5" yielding one row, or the no-match empty state
   * when all five are past. The number a chip shows is the number of rows
   * clicking it leaves behind; the cost is that a past-only area has no chip on
   * the Active tab, which the toggle already tells the user to expect.
   */
  readonly practiceAreaSummary = computed<
    { key: string; description: string; count: number }[]
  >(() => {
    // A status change moves a row between the views without touching
    // assignments(); this is how the counts learn of it.
    this.rowsVersion();
    const counts = new Map<
      string,
      { key: string; description: string; count: number }
    >();
    for (const row of this.visibleAssignments) {
      for (const pa of row.practiceAreas ?? []) {
        const key = pa.practiceCode || pa.description;
        if (!key) continue;
        const entry = counts.get(key);
        if (entry) {
          entry.count++;
        } else {
          counts.set(key, {
            key,
            description: pa.description || key,
            count: 1,
          });
        }
      }
    }
    return [...counts.values()].sort(
      (a, b) => b.count - a.count || a.description.localeCompare(b.description),
    );
  });

  /**
   * A signal, not a plain field: practiceAreaSummary counts only the current
   * view, so the computed has to see the toggle flip — and the prune effect
   * that drops selections whose chip stopped rendering has to run on it too.
   */
  readonly currentView = signal<'active' | 'past'>('active');

  // Pagination (client-side; the consumers hand the card the full list).
  // Hidden entirely while the list fits inside one default-size page.
  readonly paginationThreshold = 10;
  pageSize = 10;
  pageIndex = 0;
  readonly pageSizeOptions = [10, 25, 50];

  get activeAssignments(): AssignmentRow[] {
    return this.assignments().filter((row) => {
      const s = row.status?.toUpperCase();
      // AUTO is the synthetic latest-billed row status: recent billing is
      // current work, so those rows belong with Active, not Past.
      return s === 'ACTIVE' || s === 'PENDING' || s === 'AUTO';
    });
  }

  get pastAssignments(): AssignmentRow[] {
    return this.assignments().filter((row) => {
      const s = row.status?.toUpperCase();
      return s === 'INACTIVE' || s === 'CLOSED';
    });
  }

  get visibleAssignments(): AssignmentRow[] {
    return this.currentView() === 'active'
      ? this.activeAssignments
      : this.pastAssignments;
  }

  /**
   * Selected practice-area summary chips, keyed the same way as
   * practiceAreaSummary (practiceCode, falling back to description).
   * Multiple selections widen the filter (OR), they don't narrow it.
   */
  readonly selectedPracticeAreas = new Set<string>();

  togglePracticeArea(key: string): void {
    if (this.selectedPracticeAreas.has(key)) {
      this.selectedPracticeAreas.delete(key);
    } else {
      this.selectedPracticeAreas.add(key);
    }
    // Filter changes reset paging; page changes never reset filters.
    this.pageIndex = 0;
  }

  isPracticeAreaSelected(key: string): boolean {
    return this.selectedPracticeAreas.has(key);
  }

  /**
   * Accessible name for a summary chip. The visible label reads "Litigation - 2",
   * where the count is supplementary data rather than part of the name; spelling
   * it out keeps a screen reader from announcing the number as if it were. The
   * chips row carries the group label that says these filter the table, so the
   * chip only has to name itself.
   */
  practiceAreaChipLabel(item: { description: string; count: number }): string {
    const unit = item.count === 1 ? 'assignment' : 'assignments';
    return `${item.description}, ${item.count} ${unit}`;
  }

  /**
   * Whether the chip filter is live: its row is offered, the row filters are not
   * suspended, and the list behind the counts is not being replaced. While the
   * filter is merely suspended the chips still render — a consumer that hides the
   * text filter for its add form keeps the counts readable — but they go
   * disabled. This same predicate decides that disabled state and whether a
   * selection filters, so the control and the filtering cannot drift apart in
   * either direction. Parallel to filterVisible for the text filter.
   */
  get practiceAreaFilterEnabled(): boolean {
    return (
      this.showPracticeAreaSummary() && this.showFilter() && !this.isLoading()
    );
  }

  /**
   * visibleAssignments narrowed to the selected practice-area chips. Sits
   * upstream of the text filter so the two compose. Inert whenever the chips
   * cannot be operated — the summary row is hidden, or the row filters are
   * suspended — for the same reason a hidden text filter is (see
   * filteredAssignments): rows silently missing with no usable control to undo
   * it would read as data loss, and after an add it reads as the create having
   * failed.
   */
  get practiceAreaFilteredAssignments(): AssignmentRow[] {
    if (this.selectedPracticeAreas.size === 0 || !this.practiceAreaFilterEnabled) {
      return this.visibleAssignments;
    }
    return this.visibleAssignments.filter((row) =>
      (row.practiceAreas ?? []).some((pa) =>
        this.selectedPracticeAreas.has(pa.practiceCode || pa.description),
      ),
    );
  }

  /** Free-text row filter; matching is mode-dependent (see matchesFilter). */
  filterText = '';

  get filterVisible(): boolean {
    return this.showFilter() && this.visibleAssignments.length > 0;
  }

  get filterPlaceholder(): string {
    return this.mode() === 'matter'
      ? 'Filter by resource name'
      : 'Filter by matter name or number';
  }

  get filteredAssignments(): AssignmentRow[] {
    const query = this.filterText.trim().toLowerCase();
    // A hidden filter must not keep filtering: consumers hide it temporarily
    // (e.g. while the add-assignment form is open), and rows silently missing
    // with no visible filter box would read as data loss.
    if (!query || !this.filterVisible) {
      return this.practiceAreaFilteredAssignments;
    }
    return this.practiceAreaFilteredAssignments.filter((row) =>
      this.matchesFilter(row, query),
    );
  }

  /** Matter mode filters by the assigned resource; resource mode by the matter. */
  private matchesFilter(row: AssignmentRow, query: string): boolean {
    if (this.mode() === 'matter') {
      return (row.fullName ?? '').toLowerCase().includes(query);
    }
    return (
      (row.matterTitle ?? '').toLowerCase().includes(query) ||
      (row.clientMatterNumber ?? '').toLowerCase().includes(query)
    );
  }

  onFilterInput(event: Event): void {
    this.filterText = (event.target as HTMLInputElement).value;
    // Filter changes reset paging; page changes never reset filters.
    this.pageIndex = 0;
  }

  clearFilter(): void {
    this.filterText = '';
    this.pageIndex = 0;
  }

  /** Whether anything is currently narrowing the table. */
  get hasActiveFilters(): boolean {
    return (
      (this.filterVisible && this.filterText.trim().length > 0) ||
      (this.practiceAreaFilterEnabled && this.selectedPracticeAreas.size > 0)
    );
  }

  /**
   * Drops every active row filter at once. Offered from the no-match empty
   * state, where the combination of a chip selection and filter text can leave
   * the user with no single control that visibly undoes both. Chip selections
   * cannot reach that state on their own — the counts are view-scoped, so a
   * rendered chip always has a row behind it — but text on top of them can, and
   * an empty text box renders no clear icon of its own.
   */
  clearAllFilters(): void {
    this.resetFilters();
  }

  /**
   * The one definition of "no row filters applied" — the text, the chips, and
   * the paging that only ever moved because something was filtering. Both
   * effects that drop filters and the user-facing clearAllFilters go through
   * here; the three used to carry their own copy of these lines, which is how
   * the view-toggle reset came to be missing from one of them.
   *
   * currentView is deliberately NOT part of it. The tab is not a row filter, and
   * only a change of record resets it (see the filterResetKey effect) — folding
   * it in here would flip the tab out from under someone opening the add form,
   * which is why that one call site sets it separately rather than this taking a
   * flag that hides the difference.
   */
  private resetFilters(): void {
    this.filterText = '';
    this.selectedPracticeAreas.clear();
    this.pageIndex = 0;
  }

  get paginatorVisible(): boolean {
    return (
      this.showPaginator() &&
      this.filteredAssignments.length > this.paginationThreshold
    );
  }

  /**
   * The list can shrink under the current page (row removal, toggle flip);
   * clamping in a getter keeps the paginator and the slice in agreement
   * without mutating state from a computed path.
   */
  get clampedPageIndex(): number {
    const maxPage = Math.max(
      0,
      Math.ceil(this.filteredAssignments.length / this.pageSize) - 1,
    );
    return Math.min(this.pageIndex, maxPage);
  }

  get pagedAssignments(): AssignmentRow[] {
    const rows = this.filteredAssignments;
    if (!this.paginatorVisible) return rows;
    const start = this.clampedPageIndex * this.pageSize;
    return rows.slice(start, start + this.pageSize);
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
  }

  onViewChange(view: 'active' | 'past'): void {
    this.currentView.set(view);
    // Filter changes reset paging; page changes never reset filters.
    this.pageIndex = 0;
  }

  get currentEmptyMessage(): string {
    const override = this.emptyMessageOverride();
    if (override) return override;
    const subject =
      this.mode() === 'resource' ? 'this resource' : 'this matter';
    return this.currentView() === 'active'
      ? `No active assignments for ${subject}`
      : `No past assignments for ${subject}`;
  }

  get firstColumnHeader(): string {
    return this.mode() === 'resource' ? 'Client Matter' : 'Resource';
  }

  /**
   * How many columns the table currently renders, so the task-assignments detail row can span
   * all of them. Three of the columns are conditional, and a colspan that undercounts leaves
   * the panel boxed into part of the row's width with the rest of the row bleeding through.
   */
  readonly columnCount = computed(() => {
    // First column, assignment type, start date, end date, duration, status, actions.
    let count = 7;
    if (this.mode() !== 'resource') count++; // Practice Areas
    if (this.showRequestId()) count++;
    if (this.showTaskAssignments()) count++;
    return count;
  });

  trackByRow(_index: number, row: AssignmentRow): string {
    // assignedResourceId is unique per assignment row and is always present on
    // real rows hydrated from the backend. Synthetic SYSTEM/AUTO rows have none,
    // and in resource mode they all carry the page's employeeId — the matter
    // number is what keeps their keys distinct.
    return (
      row.assignedResourceId?.toString() ??
      `${row.requestId ?? 'direct'}-${row.employeeId ?? ''}-${row.clientMatterNumber ?? ''}`
    );
  }

  onMatterClick(clientMatterNumber: string): void {
    this.matterClicked.emit(clientMatterNumber);
  }

  onResourceClick(employeeId: string): void {
    this.resourceClicked.emit(employeeId);
  }

  onRemoveClick(row: AssignmentRow): void {
    this.removeClicked.emit(row);
  }

  /**
   * Whether the row's assignment belongs to the current user. Resolved here
   * rather than by the consumer at row-build time: user state loads
   * asynchronously after bootstrap, so a row built during a cold deep-link can
   * predate it, and nothing re-maps the rows once it arrives.
   */
  private isOwnAssignment(row: AssignmentRow): boolean {
    return (
      row.isOwnAssignment === true ||
      this.authService.isCurrentEmployee(row.employeeId)
    );
  }

  /**
   * The one exit for every in-place patch of a row — the dialog result, a row-menu
   * status change, a synthetic row created into an assignment. Invalidates what
   * derives from row fields (see rowsVersion), then reports the row to the
   * consumer. Ordered that way so a consumer reacting to the event already sees
   * the card's own derived state settled.
   */
  private rowPatched(row: AssignmentRow): void {
    this.rowsVersion.update((v) => v + 1);
    this.assignmentChanged.emit(row);
  }

  /**
   * Whether the row's ellipsis opens the row menu. For a real assignment that is
   * editors only: the "Mark X" items are status writes behind the same
   * Assignments.Edit gate as the dialog's status field, so for anyone else the menu
   * would hold nothing but "View assignment" — one more click for nothing — and the
   * ellipsis opens the dialog straight away instead. For a synthetic latest-billed
   * row it is creators only: there is no assignment to view, and the menu's items
   * create one, so the gate is Assignments.Create.
   */
  offersRowMenu(row: AssignmentRow): boolean {
    if (this.isSystemRow(row)) return this.canCreate();
    return !!row.assignedResourceId && this.canEdit();
  }

  /**
   * The "Mark X" items for a row: the lookup's options narrowed to QUICK_STATUS_CODES,
   * less the one the row already has. Marking a row with its own status would be a
   * no-op write under a success snackbar, so the item is withheld rather than
   * disabled. A pending row matches none and so gets all three — only direct pending
   * rows reach here, since request-linked pending rows render the lock instead of the
   * ellipsis. A synthetic row (status AUTO) matches none either, and there all three
   * are real choices: the assignment is created in whichever one is picked.
   */
  quickStatusOptions(row: AssignmentRow): LookupOption[] {
    const current = row.status?.toUpperCase();
    return this.statusOptions().filter((option) => {
      const code = option.value.toUpperCase();
      return QUICK_STATUS_CODES.includes(code) && code !== current;
    });
  }

  isStatusUpdating(row: AssignmentRow): boolean {
    return this.updatingRows.has(row);
  }

  /**
   * Sets the row's status from the menu, as the dialog's Save does when only the
   * status changed: the same PATCH, then the row patched in place and reported
   * through assignmentChanged, so a consumer sees exactly what a dialog save would
   * have sent it. Patched rather than remapped for the reason the dialog path gives
   * below — the expansion tracks rows by identity. A synthetic row has no status to
   * set and goes to createFromSystemRow instead.
   *
   * The row is left as it was on failure. The card holds no other account of what
   * the server has, so what it showed before the click is still the best one. The
   * server is the backstop for rows that should not be written — a request-linked
   * pending row, were one ever to reach here, is refused with a 409 by the
   * assignment service's own editability check — and its message is what the
   * snackbar shows.
   */
  markStatus(row: AssignmentRow, option: LookupOption): void {
    if (this.updatingRows.has(row)) return;
    if (this.isSystemRow(row)) {
      this.createFromSystemRow(row, option);
      return;
    }

    const id = row.assignedResourceId;
    if (!id || !this.canEdit()) return;

    this.updatingRows.add(row);
    this.assignmentService
      .updateAssignmentStatus(id, option.value)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.updatingRows.delete(row)),
      )
      .subscribe({
        next: () => {
          row.status = option.value;
          row.statusName = option.label;
          this.snackbar.showSuccess(`Assignment marked ${option.label}`);
          this.rowPatched(row);
        },
        error: (error) => {
          this.snackbar.showError(
            describeApiError(error, `Failed to mark assignment ${option.label}`),
          );
        },
      });
  }

  /**
   * Turns a synthetic latest-billed row into a real assignment in the chosen status:
   * a direct (no request) PRIMARY assignment starting on the row's first-billed date,
   * with no end date — the last-billed date is enrichment, not an end. One call, with
   * the status on the create, rather than a create followed by a status PATCH that
   * could fail and leave an ACTIVE row nobody asked for.
   *
   * The response is the row as the API lists it — in the duplicate race, the row
   * another user created first, which can be request-linked and carry notes — so
   * everything the row shows is mapped from it, through the same mapping the hosts
   * build rows with (toAssignmentRow). A pending request-linked row then gets its lock
   * and its Request # link, and the dialog opens seeded with the note that exists,
   * rather than the card contradicting the response it just quoted. The mode-specific
   * fields stay the page's: a resource-mode row keeps its matter names, and the
   * last-billed enrichment survives where the response carries none — it is still the
   * same resource on the same matter. Patched in place, as every other path here does.
   */
  private createFromSystemRow(
    row: AssignmentRow,
    option: LookupOption,
  ): void {
    const { clientMatterNumber, employeeId } = row;
    if (!clientMatterNumber || !employeeId || !this.canCreate()) return;

    this.updatingRows.add(row);
    this.assignmentService
      .createDirectAssignment(
        clientMatterNumber,
        employeeId,
        this.firstBilledStartDate(row.startDate),
        null,
        option.value,
      )
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.updatingRows.delete(row)),
      )
      .subscribe({
        next: (created) => {
          Object.assign(
            row,
            toAssignmentRow(created, {
              clientMatterNumber: row.clientMatterNumber,
              clientName: row.clientName,
              matterTitle: row.matterTitle,
              employeeId: row.employeeId,
              fullName: row.fullName,
              title: row.title,
              dialogTitle: row.dialogTitle,
              resourceName: row.resourceName,
              isOwnAssignment: row.isOwnAssignment,
              practiceAreas: row.practiceAreas,
              lastBilledDate: created.lastBilledDate ?? row.lastBilledDate,
            }),
          );
          this.snackbar.showSuccess(
            `Assignment created and marked ${row.statusName || option.label}`,
          );
          this.rowPatched(row);
        },
        error: (error) => {
          this.snackbar.showError(
            describeApiError(error, 'Failed to create assignment'),
          );
        },
      });
  }

  /**
   * A synthetic row's first-billed date as the start date to persist. The API pins
   * that value to UTC midnight and the card renders it in UTC for exactly that reason
   * (see formatUtcDate) — but a real row's start date renders in local time, so
   * persisting the UTC instant as-is would show the previous day to any viewer west of
   * UTC once the row is real. Re-anchoring the same calendar date to local midnight is
   * what the add form sends for a date picked in the datepicker, so the created row
   * reads the same as one added by hand.
   */
  private firstBilledStartDate(
    value: Date | string | undefined,
  ): string | null {
    if (!value) return null;
    const utc = new Date(value);
    if (isNaN(utc.getTime())) return null;
    return new Date(
      utc.getUTCFullYear(),
      utc.getUTCMonth(),
      utc.getUTCDate(),
    ).toISOString();
  }

  /**
   * Opens the edit dialog for a row: from the menu's "Edit assignment" item for
   * editors, straight from the ellipsis for everyone else (see offersRowMenu).
   *
   * Pending request-sourced assignments never reach this method — the action cell
   * renders a disabled lock for them instead of the ellipsis. The server is the
   * backstop either way — the assignment service refuses every status, date and
   * type write to such a row with a 409, and the dialog surfaces the message — so
   * the gate is not duplicated here.
   */
  openEditDialog(row: AssignmentRow): void {
    if (!row.assignedResourceId) return;

    const isOwnAssignment = this.isOwnAssignment(row);

    const data: AssignmentEditDialogData = {
      assignedResourceId: row.assignedResourceId,
      requestId: row.requestId,
      clientMatterNumber: row.clientMatterNumber,
      resourceName:
        this.mode() === 'resource'
          ? row.matterTitle || row.resourceName || row.fullName || ''
          : row.resourceName || row.fullName || '',
      dialogTitle: row.dialogTitle,
      currentStatus: row.status,
      currentAssignmentType: row.assignmentTypeCategory,
      startDate: row.startDate ?? null,
      endDate: row.endDate ?? null,
      notesInfo: row.notesInfo ?? null,
      resourceNotesInfo: row.resourceNotesInfo ?? null,
      canEdit: this.canEdit(),
      canRemove: this.canRemove(),
      showManagerNotes:
        this.showManagerNotes() && row.canViewResource !== false,
      // Writing also requires the note to be visible: with the content withheld the
      // field renders empty, and saving that would blank out a note the user cannot read.
      canEditManagerNotes:
        this.canWriteManagerNotes() && row.canViewResource !== false,
      showResourceNotes:
        isOwnAssignment ||
        (this.canReadResourceNotes() && row.canViewResource !== false),
      canEditResourceNotes: isOwnAssignment,
      // The same flag the expand column rides on, resolved once here rather than
      // fetched again by the dialog. The list is copied through as an array so the
      // dialog starts from what the row shows; `?? []` because a row that has never
      // carried tasks still has a Tasks section to open.
      taskAssignments: row.taskAssignments ?? [],
      showTaskAssignments: this.showTaskAssignments(),
    };

    const ref = this.dialog.open<
      AssignmentEditDialogComponent,
      AssignmentEditDialogData,
      AssignmentEditDialogResult | null
    >(AssignmentEditDialogComponent, {
      width: '640px',
      maxHeight: '90vh',
      autoFocus: false,
      disableClose: true,
      data,
    });

    // The dialog outlives a card that navigates away beneath it; its result then has
    // no row to land on (see destroyRef).
    ref
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
      if (!result) return;

      if (result.removed) {
        this.assignmentRemoved.emit(row);
        return;
      }

      if (result.status) {
        row.status = result.status;
        row.statusName = result.statusName ?? row.statusName;
      }
      if (result.assignmentTypeCategory) {
        row.assignmentTypeCategory = result.assignmentTypeCategory;
        row.assignmentTypeCategoryName =
          result.assignmentTypeCategoryName ?? row.assignmentTypeCategoryName;
      }
      if (result.startDate !== undefined) {
        row.startDate = result.startDate ?? undefined;
      }
      if (result.endDate !== undefined) {
        row.endDate = result.endDate ?? undefined;
      }
      if (result.notesInfo !== undefined) {
        row.notesInfo = result.notesInfo ?? null;
        row.hasNotes = !!result.notesInfo?.notes;
      }
      if (result.resourceNotesInfo !== undefined) {
        row.resourceNotesInfo = result.resourceNotesInfo ?? null;
        row.hasResourceNotes = !!result.resourceNotesInfo?.notes;
      }
      // The full list after the save, so an empty array is a real answer and must
      // land — undefined alone means the dialog did not touch tasks (same
      // convention as the two note fields). Patched onto the row rather than
      // remapped into a new one: the expansion tracks rows by identity, and a
      // replacement object would silently close the panel the user just saved from.
      if (result.taskAssignments !== undefined) {
        row.taskAssignments = result.taskAssignments;
      }
      this.rowPatched(row);
    });
  }

  isOverdueActive(assignment: AssignmentRow): boolean {
    if (assignment.status?.toUpperCase() !== 'ACTIVE') return false;
    if (!assignment.endDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const end = new Date(assignment.endDate);
    end.setHours(0, 0, 0, 0);
    return end < today;
  }

  getAssignmentLengthInWeeks(assignment: AssignmentRow): number | null {
    if (!assignment.startDate || !assignment.endDate) return null;
    const start = new Date(assignment.startDate);
    const end = new Date(assignment.endDate);
    const diffDays = (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);
    return Math.ceil(diffDays / 7);
  }

  getStatusClass(status: string): string {
    const normalized = status?.toLowerCase() || '';
    switch (normalized) {
      case 'new':
        return 'badge-new';
      case 'active':
        return 'badge-active';
      case 'pending':
        return 'badge-pending';
      case 'inactive':
        return 'badge-inactive';
      case 'closed':
        return 'badge-closed';
      case 'fulfilled':
        return 'badge-fulfilled';
      case 'auto':
        return 'badge-auto';
      default:
        return 'badge-default';
    }
  }

  formatDate(date: Date | string | undefined): string {
    if (!date) return 'Not set';
    const d = new Date(date);
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  /** Whether the row is a synthetic latest-billed row rather than a real assignment. */
  isSystemRow(row: AssignmentRow): boolean {
    return row.assignmentTypeCategory === 'SYSTEM';
  }

  /**
   * Formatter for the latest-billed dates (lastBilledDate, and a system row's
   * first-billed start date): the API pins these date-only values to UTC
   * midnight, so they must render in UTC — through the local-time formatDate,
   * any viewer west of UTC would see the previous day.
   */
  formatUtcDate(date: Date | string): string {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC',
    });
  }
}
